import type { ZodType } from 'zod'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export type MutationMethod = 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface ApiClient {
  get<T>(path: string, schema: ZodType<T>): Promise<T>
  /** GET a same-origin BFF handler (`/bff/*`: direct Neon/KV reads), Clerk-authed like `get`. */
  bffGet<T>(path: string, schema: ZodType<T>): Promise<T>
  /** Non-GET call through the BFF. JSON body is serialized; the response is validated by `schema`. */
  request<T>(path: string, init: { method: MutationMethod; body?: unknown }, schema: ZodType<T>): Promise<T>
}

export function createApiClient(opts: { getToken: () => Promise<string | null>; fetchImpl?: typeof fetch }): ApiClient {
  const doFetch = opts.fetchImpl ?? fetch

  async function call<T>(
    path: string,
    init: RequestInit,
    extraHeaders: Record<string, string>,
    schema: ZodType<T>,
    prefix = '/api',
  ) {
    const token = await opts.getToken()
    const res = await doFetch(`${prefix}${path}`, {
      ...init,
      headers: { Accept: 'application/json', ...extraHeaders, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    if (!res.ok) {
      let body: unknown
      try {
        body = await res.json()
      } catch {
        body = await res.text().catch(() => undefined)
      }
      throw new ApiError(res.status, `API ${res.status} for ${path}`, body)
    }
    const json = await res.json()
    return schema.parse(json) as T
  }

  return {
    get: (path, schema) => call(path, {}, {}, schema),
    bffGet: (path, schema) => call(path, {}, {}, schema, '/bff'),
    request: (path, { method, body }, schema) =>
      call(
        path,
        { method, ...(body !== undefined ? { body: JSON.stringify(body) } : {}) },
        body !== undefined ? { 'Content-Type': 'application/json' } : {},
        schema,
      ),
  }
}
